import type { Root, Element } from 'hast';

/**
 * Rehype 插件：将未知的 HTML 标签转换为 span 标签
 * 这样可以避免 React 报错，同时保留标签的内容和属性
 * 
 * 标准 HTML 标签列表（React 支持的标签）
 * 如果标签名不在这个列表中，将被转换为 span 标签
 */
const VALID_HTML_TAGS = new Set([
	'a', 'abbr', 'address', 'area', 'article', 'aside', 'audio',
	'b', 'base', 'bdi', 'bdo', 'blockquote', 'body', 'br', 'button',
	'canvas', 'caption', 'cite', 'code', 'col', 'colgroup',
	'data', 'datalist', 'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt',
	'em', 'embed',
	'fieldset', 'figcaption', 'figure', 'footer', 'form',
	'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html',
	'i', 'iframe', 'img', 'input', 'ins',
	'kbd',
	'label', 'legend', 'li', 'link',
	'main', 'map', 'mark', 'menu', 'meta', 'meter',
	'nav', 'noscript',
	'object', 'ol', 'optgroup', 'option', 'output',
	'p', 'param', 'picture', 'pre', 'progress',
	'q',
	'rp', 'rt', 'ruby',
	's', 'samp', 'script', 'section', 'select', 'slot', 'small', 'source', 'span', 'strong', 'style', 'sub', 'summary', 'sup',
	'table', 'tbody', 'td', 'template', 'textarea', 'tfoot', 'th', 'thead', 'time', 'title', 'tr', 'track',
	'u', 'ul',
	'var', 'video',
	'wbr',
]);

/**
 * Rehype 插件：将未知的 HTML 标签转换为 span 标签
 * 
 * 处理逻辑：
 * 1. 递归遍历所有元素节点（深度优先）
 * 2. 检查标签名是否为有效的 HTML 标签
 * 3. 如果不是，将其转换为 span 标签，保留所有属性和子节点
 * 
 * @example
 * // 输入：<scp>text</scp>
 * // 输出：<span>text</span>
 */
export function rehypeNormalizeUnknownTags() {
	return (tree: Root) => {
		// 递归遍历 AST（深度优先）
		function traverse(node: any) {
			if (!node || typeof node !== 'object') return;

			// 处理元素节点
			if (node.type === 'element') {
				const element = node as Element;
				const tagName = element.tagName?.toLowerCase();

				// 检查标签名是否为有效的 HTML 标签
				// 如果标签名不在有效列表中，转换为 span
				if (tagName && !VALID_HTML_TAGS.has(tagName)) {
					element.tagName = 'span';
				}

				// 递归处理所有子节点
				if (element.children) {
					element.children.forEach(traverse);
				}
			}
		}

		// 从根节点开始遍历
		if (tree.children) {
			tree.children.forEach(traverse);
		}
	};
}
